import { useEffect, useState } from "react";
import { fetchNineBox, calibrateNineBox, resetNineBoxCalibration, createNineBoxSnapshot, listNineBoxSnapshots, compareNineBoxSnapshots, deleteNineBoxSnapshot } from "../lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import EmployeeDetailModal from "./EmployeeDetailModal";
import { toast } from "sonner";
import { Loader2, Users, Sparkles, TrendingUp, AlertTriangle, Camera, GitCompare, RotateCcw, ArrowUp, ArrowDown, ArrowRight, Trash2, Info } from "lucide-react";

const PRIORITY_ICON = {
  hipo: <Sparkles className="w-3.5 h-3.5" />,
  koru: <TrendingUp className="w-3.5 h-3.5" />,
  izlemede: <Users className="w-3.5 h-3.5" />,
  aksiyon: <AlertTriangle className="w-3.5 h-3.5" />,
};

export default function NineBoxMatrix() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [department, setDepartment] = useState("all");
  const [selectedCell, setSelectedCell] = useState(null);
  const [mode, setMode] = useState("view"); // 'view' | 'calibrate' | 'compare'
  const [dragEmp, setDragEmp] = useState(null);
  const [calibDialog, setCalibDialog] = useState(null); // { employee, targetCell, rationale }
  const [snapshots, setSnapshots] = useState([]);
  const [snapshotDialog, setSnapshotDialog] = useState(false);
  const [snapshotLabel, setSnapshotLabel] = useState("");
  const [snapshotNotes, setSnapshotNotes] = useState("");
  const [compareFromId, setCompareFromId] = useState("");
  const [compareToId, setCompareToId] = useState("__live__");
  const [compareResult, setCompareResult] = useState(null);
  const [detailEmpId, setDetailEmpId] = useState(null);

  const reload = () => {
    setLoading(true);
    const params = department !== "all" ? { department } : {};
    fetchNineBox(params).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  };

  useEffect(reload, [department]);

  useEffect(() => {
    listNineBoxSnapshots().then((d) => setSnapshots(d.snapshots || [])).catch(() => setSnapshots([]));
  }, []);

  const handleDrop = (targetX, targetY) => {
    if (!dragEmp) return;
    setDragEmp(null);
    // Aynı hücreye bırakılırsa hiçbir şey yapma
    if (dragEmp._sourceX === targetX && dragEmp._sourceY === targetY) return;
    setCalibDialog({ employee: dragEmp, targetCell: { x: targetX, y: targetY }, rationale: "" });
  };

  const submitCalibration = async () => {
    if (!calibDialog?.rationale?.trim()) {
      toast.error("Kalibrasyon gerekçesi zorunlu");
      return;
    }
    try {
      await calibrateNineBox({
        employee_id: calibDialog.employee.id,
        cell_x: calibDialog.targetCell.x,
        cell_y: calibDialog.targetCell.y,
        rationale: calibDialog.rationale,
        calibrated_by: "hr",
      });
      toast.success(`${calibDialog.employee.name} yeni kutuya kalibre edildi`);
      setCalibDialog(null);
      reload();
    } catch {
      toast.error("Kalibrasyon başarısız");
    }
  };

  const resetCalibration = async (empId, empName) => {
    try {
      await resetNineBoxCalibration(empId);
      toast.success(`${empName} otomatik konumuna geri döndürüldü`);
      reload();
    } catch {
      toast.error("Sıfırlama başarısız");
    }
  };

  const saveSnapshot = async () => {
    if (!snapshotLabel.trim()) {
      toast.error("Snapshot adı zorunlu");
      return;
    }
    try {
      const snap = await createNineBoxSnapshot({ label: snapshotLabel, notes: snapshotNotes, created_by: "hr" });
      toast.success(`Snapshot alındı: ${snap.label}`);
      setSnapshotLabel("");
      setSnapshotNotes("");
      setSnapshotDialog(false);
      const list = await listNineBoxSnapshots();
      setSnapshots(list.snapshots || []);
    } catch {
      toast.error("Snapshot alınamadı");
    }
  };

  const runCompare = async () => {
    if (!compareFromId) { toast.error("Kaynak snapshot seç"); return; }
    try {
      const to = compareToId === "__live__" ? null : compareToId;
      const res = await compareNineBoxSnapshots(compareFromId, to);
      setCompareResult(res);
    } catch {
      toast.error("Karşılaştırma başarısız");
    }
  };

  const removeSnapshot = async (id) => {
    if (!window.confirm("Snapshot silinsin mi?")) return;
    await deleteNineBoxSnapshot(id);
    const list = await listNineBoxSnapshots();
    setSnapshots(list.snapshots || []);
  };

  if (loading) return <div className="card-surface p-8 flex items-center justify-center gap-3 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Yetenek matrisi hesaplanıyor...</div>;
  if (!data) return <div className="card-surface p-6 text-slate-500">Veri yüklenemedi.</div>;

  const grid = data.grid || [];
  const total = data.total_employees || 0;
  const calibrationCount = data.calibration_count || 0;

  return (
    <div className="space-y-5" data-testid="nine-box-matrix">
      {/* Header + filters + mode toggle */}
      <div className="card-surface p-5 flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="label-eyebrow flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-indigo-600" /> 9 Box · Performans × Potansiyel Matrisi
          </div>
          <h2 className="text-2xl font-semibold text-slate-900 mt-1">Yetenek Haritası</h2>
          <p className="text-sm text-slate-600 mt-1">
            {total} aktif çalışan otomatik konumlandırıldı. {calibrationCount > 0 && (
              <span className="text-indigo-700 font-medium">{calibrationCount} manuel kalibrasyon aktif.</span>
            )}
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <div className="text-xs text-slate-500 mb-1">Departman</div>
            <Select value={department} onValueChange={setDepartment}>
              <SelectTrigger className="w-[200px] bg-white border-slate-200" data-testid="nine-box-department-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="all">Tüm Departmanlar</SelectItem>
                {(data.filters?.departments || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant={mode === "calibrate" ? "default" : "outline"} size="sm" onClick={() => setMode(mode === "calibrate" ? "view" : "calibrate")} data-testid="nine-box-mode-calibrate">
            🎯 Kalibrasyon
          </Button>
          <Button variant="outline" size="sm" onClick={() => setSnapshotDialog(true)} data-testid="nine-box-snapshot-btn">
            <Camera className="w-3.5 h-3.5 mr-1" /> Snapshot Al
          </Button>
          <Button variant={mode === "compare" ? "default" : "outline"} size="sm" onClick={() => setMode(mode === "compare" ? "view" : "compare")} data-testid="nine-box-mode-compare">
            <GitCompare className="w-3.5 h-3.5 mr-1" /> Karşılaştır
          </Button>
        </div>
      </div>

      {mode === "calibrate" && (
        <div className="rounded-lg p-3 bg-indigo-50 border border-indigo-100 text-sm text-indigo-900 flex items-center gap-2">
          <Info className="w-4 h-4 flex-shrink-0" />
          Kalibrasyon modu aktif — bir çalışanı farklı kutuya sürükleyip bırak; kaynak+gerekçe kaydedilir. YTN-05, YTN-08 uyumlu.
        </div>
      )}

      {/* Matrix */}
      <div className="card-surface p-5">
        <div className="flex gap-2">
          <div className="w-8 flex flex-col justify-between text-xs text-slate-500 py-2 [writing-mode:vertical-rl] rotate-180 items-center">
            <span>Potansiyel →</span>
          </div>
          <div className="flex-1">
            <div className="grid grid-cols-3 gap-2">
              {grid.map((cell) => (
                <div
                  key={`${cell.x}-${cell.y}`}
                  data-testid={`nine-box-cell-${cell.x}-${cell.y}`}
                  onDragOver={(e) => { if (mode === "calibrate") e.preventDefault(); }}
                  onDrop={() => mode === "calibrate" && handleDrop(cell.x, cell.y)}
                  className={`text-left rounded-xl p-3 border-2 transition-all min-h-[170px] ${mode === "calibrate" ? "ring-1 ring-indigo-200 hover:ring-indigo-400" : ""}`}
                  style={{ borderColor: cell.color + "40", background: cell.color + "08" }}
                >
                  <button onClick={() => setSelectedCell(cell)} className="w-full text-left">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: cell.color + "20", color: cell.color }}>
                        {PRIORITY_ICON[cell.priority] || null}
                        {cell.priority === "hipo" ? "HiPo" : cell.priority === "koru" ? "Koru" : cell.priority === "aksiyon" ? "Aksiyon" : "İzle"}
                      </span>
                    </div>
                    <div className="text-sm font-semibold text-slate-800 leading-tight">{cell.label}</div>
                    <div className="mt-2 text-3xl font-bold" style={{ color: cell.color }}>{cell.count}</div>
                    <div className="text-xs text-slate-500 mt-0.5">çalışan</div>
                  </button>
                  {cell.employees.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {cell.employees.slice(0, 6).map((e) => (
                        <div
                          key={e.id}
                          draggable={mode === "calibrate"}
                          onDragStart={() => setDragEmp({ ...e, _sourceX: cell.x, _sourceY: cell.y })}
                          onClick={() => mode !== "calibrate" && setSelectedCell(cell)}
                          className={`relative ${mode === "calibrate" ? "cursor-grab active:cursor-grabbing" : ""}`}
                          title={`${e.name} ${e.calibrated ? "(kalibre edilmiş)" : ""}`}
                        >
                          <img src={e.avatar} alt={e.name} className="w-7 h-7 rounded-full border-2 border-white object-cover" onError={(ev) => { ev.currentTarget.style.display = "none"; }} />
                          {e.calibrated && (
                            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-indigo-500 border border-white" title="Kalibre edilmiş" />
                          )}
                        </div>
                      ))}
                      {cell.count > 6 && (
                        <span className="w-7 h-7 rounded-full border-2 border-white bg-slate-100 text-[10px] font-medium text-slate-600 flex items-center justify-center">+{cell.count - 6}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2 text-center text-xs text-slate-500">
              {(data.axes?.x_ticks || ["Düşük", "Orta", "Yüksek"]).map((t) => <div key={t}>{t}</div>)}
            </div>
            <div className="text-center text-xs text-slate-500 mt-1">Performans →</div>
          </div>
        </div>
      </div>

      {/* Compare panel */}
      {mode === "compare" && (
        <div className="card-surface p-5 space-y-4" data-testid="nine-box-compare-panel">
          <div className="label-eyebrow flex items-center gap-2"><GitCompare className="w-3.5 h-3.5 text-indigo-600" /> Snapshot Karşılaştırma (YTN-11)</div>
          {snapshots.length < 1 ? (
            <div className="text-sm text-slate-500 italic">Henüz snapshot yok. Önce &quot;Snapshot Al&quot; ile bir görüntü kaydet.</div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <div>
                  <div className="text-xs text-slate-500 mb-1">Kaynak (from)</div>
                  <Select value={compareFromId} onValueChange={setCompareFromId}>
                    <SelectTrigger className="w-[280px] bg-white"><SelectValue placeholder="Snapshot seç..." /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {snapshots.map((s) => <SelectItem key={s.id} value={s.id}>{s.label} · {new Date(s.created_at).toLocaleDateString("tr-TR")}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Hedef (to)</div>
                  <Select value={compareToId} onValueChange={setCompareToId}>
                    <SelectTrigger className="w-[280px] bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="__live__">Şu an (canlı durum)</SelectItem>
                      {snapshots.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button size="sm" onClick={runCompare} data-testid="nine-box-run-compare">Karşılaştır</Button>
              </div>
              {compareResult && (
                <div className="space-y-3 pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-3 text-sm">
                    <span className="font-medium text-slate-700">{compareResult.from.label}</span>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                    <span className="font-medium text-slate-700">{compareResult.to.label}</span>
                    <span className="ml-auto text-xs text-slate-500">
                      {compareResult.summary.moved} hareket · ↑ {compareResult.summary.up} · ↓ {compareResult.summary.down} · → {compareResult.summary.sideways}
                    </span>
                  </div>
                  {/* Cell deltas grid */}
                  <div className="grid grid-cols-3 gap-2">
                    {compareResult.cell_deltas.sort((a, b) => (b.y - a.y) || (a.x - b.x)).map((d) => (
                      <div key={`${d.x}-${d.y}`} className="p-2 rounded border" style={{ borderColor: d.color + "30", background: d.color + "08" }}>
                        <div className="text-[10px] uppercase tracking-wide text-slate-500">{d.label}</div>
                        <div className="text-sm font-medium">
                          {d.from} → <span style={{ color: d.color }}>{d.to}</span>
                          {d.delta !== 0 && (
                            <span className={`ml-2 text-xs font-bold ${d.delta > 0 ? "text-emerald-600" : "text-red-600"}`}>
                              {d.delta > 0 ? `+${d.delta}` : d.delta}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Employee moves */}
                  {compareResult.moves.length > 0 && (
                    <div>
                      <div className="label-eyebrow mb-2">Hareket Eden Çalışanlar ({compareResult.moves.length})</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-72 overflow-y-auto">
                        {compareResult.moves.map((m) => (
                          <div key={m.employee_id} className="flex items-center gap-2 p-2 rounded bg-slate-50 border border-slate-100">
                            <img src={m.avatar} alt={m.name} className="w-8 h-8 rounded-full object-cover" onError={(ev) => (ev.currentTarget.style.visibility = "hidden")} />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{m.name}</div>
                              <div className="text-[10px] text-slate-500 truncate">{m.title}</div>
                            </div>
                            <div className="flex items-center gap-1 text-xs">
                              <span className="text-slate-400">({m.from_cell.x},{m.from_cell.y})</span>
                              {m.direction === "up" ? <ArrowUp className="w-3.5 h-3.5 text-emerald-600" /> : m.direction === "down" ? <ArrowDown className="w-3.5 h-3.5 text-red-600" /> : <ArrowRight className="w-3.5 h-3.5 text-slate-400" />}
                              <span className="text-slate-700 font-medium">({m.to_cell.x},{m.to_cell.y})</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Snapshot list */}
              <div className="pt-3 border-t border-slate-100">
                <div className="label-eyebrow mb-2">Snapshot Listesi ({snapshots.length})</div>
                <div className="space-y-1">
                  {snapshots.map((s) => (
                    <div key={s.id} className="flex items-center gap-2 p-2 rounded bg-slate-50 border border-slate-100">
                      <div className="flex-1">
                        <div className="text-sm font-medium">{s.label}</div>
                        <div className="text-[10px] text-slate-500">
                          {new Date(s.created_at).toLocaleString("tr-TR")} · {s.total_employees} çalışan · {s.created_by}
                        </div>
                        {s.notes && <div className="text-xs text-slate-600 mt-0.5 italic">{s.notes}</div>}
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => removeSnapshot(s.id)}><Trash2 className="w-3.5 h-3.5 text-slate-400" /></Button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Cell detail drawer */}
      {selectedCell && (
        <div className="card-surface p-5 border-2" style={{ borderColor: selectedCell.color + "40" }} data-testid="nine-box-cell-detail">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="label-eyebrow">Seçili Kutu</div>
              <h3 className="text-lg font-semibold" style={{ color: selectedCell.color }}>{selectedCell.label} ({selectedCell.count} çalışan)</h3>
            </div>
            <button onClick={() => setSelectedCell(null)} className="text-xs text-slate-500 hover:text-slate-700" data-testid="nine-box-close-cell">Kapat ✕</button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {selectedCell.employees.map((e) => (
              <button
                type="button"
                key={e.id}
                onClick={() => setDetailEmpId(e.id)}
                data-testid={`nine-box-emp-${e.id}`}
                className="flex items-center gap-3 p-2.5 rounded-lg bg-white border border-slate-100 hover:border-indigo-300 hover:shadow-sm text-left transition-all"
              >
                <img src={e.avatar} alt={e.name} className="w-10 h-10 rounded-full object-cover" onError={(ev) => (ev.currentTarget.style.visibility = "hidden")} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1">
                    <div className="text-sm font-medium truncate">{e.name}</div>
                    {e.calibrated && <span className="text-[9px] px-1 py-0.5 rounded bg-indigo-50 text-indigo-700 font-medium">Kalibre</span>}
                  </div>
                  <div className="text-xs text-slate-500 truncate">{e.title}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">Perf: {e.performance_rating?.toFixed(1) || "–"} · Pot: {e.potential || "–"}</div>
                  {e.reasoning?.summary && (
                    <div className="text-[10px] text-slate-600 mt-1 italic line-clamp-2">
                      {e.reasoning.summary}
                    </div>
                  )}
                  {e.calibrated && e.calibration_rationale && (
                    <div className="text-[10px] text-indigo-700 mt-1 italic">Gerekçe: {e.calibration_rationale}</div>
                  )}
                </div>
                {e.calibrated && (
                  <span
                    title="Otomatik konuma döndür"
                    role="button"
                    onClick={(ev) => { ev.stopPropagation(); resetCalibration(e.id, e.name); }}
                    className="text-slate-400 hover:text-red-500 cursor-pointer"
                    data-testid={`nine-box-reset-${e.id}`}
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Calibration Dialog */}
      <Dialog open={!!calibDialog} onOpenChange={(v) => !v && setCalibDialog(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="nine-box-calibrate-dialog">
          <DialogHeader>
            <DialogTitle>Manuel Kalibrasyon — Gerekçe</DialogTitle>
          </DialogHeader>
          {calibDialog && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <img src={calibDialog.employee.avatar} alt={calibDialog.employee.name} className="w-10 h-10 rounded-full object-cover" />
                <div>
                  <div className="font-medium">{calibDialog.employee.name}</div>
                  <div className="text-xs text-slate-500">{calibDialog.employee.title}</div>
                  <div className="text-xs text-slate-500 mt-1">
                    Kaynak: ({calibDialog.employee._sourceX},{calibDialog.employee._sourceY}) → Hedef: ({calibDialog.targetCell.x},{calibDialog.targetCell.y})
                  </div>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Kalibrasyon gerekçesi (zorunlu)</label>
                <Textarea
                  value={calibDialog.rationale}
                  onChange={(e) => setCalibDialog({ ...calibDialog, rationale: e.target.value })}
                  rows={4}
                  placeholder="Örn: Son 6 ayda satış kapama oranı %25 arttı; potansiyeli data sistemine yansımadan yükseldi. Kalibrasyon jürisi kararı."
                  data-testid="nine-box-calibrate-rationale"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCalibDialog(null)}>İptal</Button>
            <Button onClick={submitCalibration} data-testid="nine-box-calibrate-submit">Kalibrasyonu Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Snapshot Dialog */}
      <Dialog open={snapshotDialog} onOpenChange={setSnapshotDialog}>
        <DialogContent className="bg-white max-w-lg" data-testid="nine-box-snapshot-dialog">
          <DialogHeader>
            <DialogTitle>9 Box Snapshot Al</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-700">Snapshot adı</label>
              <Input value={snapshotLabel} onChange={(e) => setSnapshotLabel(e.target.value)} placeholder="Örn: Q1 2026 Kalibrasyonu Sonrası" data-testid="nine-box-snapshot-label" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-700">Not (opsiyonel)</label>
              <Textarea value={snapshotNotes} onChange={(e) => setSnapshotNotes(e.target.value)} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSnapshotDialog(false)}>İptal</Button>
            <Button onClick={saveSnapshot} data-testid="nine-box-snapshot-submit">Snapshot Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Employee Detail Modal */}
      <EmployeeDetailModal
        employeeId={detailEmpId}
        open={!!detailEmpId}
        onOpenChange={(v) => !v && setDetailEmpId(null)}
      />
    </div>
  );
}
